export const KAVACH_COLORS = {
  purple: "#4B4495",
  purpleDark: "#2F2963",
  indigo: "#18406E",
  gold: "#FFD700",
  goldDark: "#FFA500",
  green: "#2EA967",
  bg: "#F2F4FA",
  ink: "#16283A",
  slate: "#5C6B7A",
  card: "#FFFFFF",
};

const TITLE_ICON_RULES = [
  [/heater/i, "heater"],
  [/\biron\b/i, "iron"],
  [/ladder/i, "ladder"],
  [/cooler/i, "cooler"],
  [/washing machine/i, "washingMachine"],
  [/iphone|phone/i, "smartphone"],
  [/trip|goa|thailand/i, "plane"],
  [/activa|scooter/i, "scooter"],
  [/bike|motorbike/i, "motorbike"],
];

export const pickOptionIcon = (option) => {
  if (!option) return "gift";
  if (option.optionType === "COIN_EQUIVALENT") return "sparkle";
  const match = TITLE_ICON_RULES.find(([re]) => re.test(option.title || ""));
  return match ? match[1] : "gift";
};

const clamp01 = (n) => Math.max(0, Math.min(1, n));

export const buildKavachViewModel = (
  summary,
  saveoCreditAmount = 0,
  kavachCoinCreditAmount = 0,
) => {
  const {
    totalPoints,
    totalCoins,
    currentLevel,
    nextLevel,
    canClaim,
    claimableOptions,
    ladder,
  } = summary;

  const totalBalance = saveoCreditAmount ?? 0;

  const milestones = (ladder || []).map((level, i) => {
    const isLast = i === ladder.length - 1;
    const options = level.options || [];
    const title = options.length
      ? options.map((o) => o.title).join(" / ")
      : `Level ${level.levelNumber}`;
    const icon = pickOptionIcon(options[0]);

    let status = "locked";
    if (currentLevel && level.levelNumber < currentLevel.levelNumber) {
      status = "claimed";
    } else if (currentLevel && level.levelNumber === currentLevel.levelNumber) {
      status = canClaim ? "current" : "claimed";
    } else if (
      !canClaim &&
      nextLevel &&
      level.levelNumber === nextLevel.levelNumber
    ) {
      status = "current";
    }

    return {
      id: level.levelNumber,
      step: level.levelNumber,
      title,
      points: level.pointsThreshold,
      icon,
      status,
      side: isLast ? "center" : i % 2 === 0 ? "left" : "right",
      grand: isLast,
      remaining:
        status === "current"
          ? canClaim
            ? (currentLevel?.pointsToGo ?? 0)
            : nextLevel?.pointsToGo
          : undefined,
      options,
    };
  });

  const currentMilestoneIndex = milestones.findIndex(
    (m) => m.status === "current",
  );

  const localFrac = nextLevel
    ? clamp01(
        (totalPoints - (currentLevel?.pointsThreshold ?? 0)) /
          (nextLevel.pointsThreshold - (currentLevel?.pointsThreshold ?? 0)),
      )
    : 1;

  const currentLevelIndex = currentLevel
    ? milestones.findIndex((m) => m.step === currentLevel.levelNumber)
    : -1;
  const reachedCount = currentLevelIndex === -1 ? 0 : currentLevelIndex + 1;

  const overallFrac = milestones.length
    ? clamp01((reachedCount + localFrac) / milestones.length)
    : 0;

  const kpiCards = [
    {
      id: "level",
      label: "Current Level",
      icon: "medal",
      value: currentLevel?.levelNumber
        ? `Level ${currentLevel.levelNumber}`
        : "Start",
      sub: `of ${milestones.length} levels`,
      accent: KAVACH_COLORS.gold,
    },
    {
      id: "points",
      label: "Current Points",
      icon: "sparkle",
      value: totalPoints.toLocaleString("en-IN"),
      accent: KAVACH_COLORS.purple,
    },
    {
      id: "coins",
      label: "CureCoins Summary",
      icon: "wallet",
      value: totalBalance.toLocaleString("en-IN"),
      hideValue: true,
      accent: KAVACH_COLORS.green,
      breakdown: [
        {
          label: "Kavach Amount",
          value: (kavachCoinCreditAmount ?? 0).toLocaleString("en-IN"),
        },
        {
          label: "CureCoin Amount",
          value: (saveoCreditAmount ?? 0).toLocaleString("en-IN"),
        },
      ],
    },
    {
      id: "history",
      label: "History",
      icon: "orders",
      value: "View Transaction History",
      accent: KAVACH_COLORS.indigo,
    },
  ];

  const quickStats = [
    {
      id: "points",
      icon: "sparkle",
      label: "Total Points",
      value: totalPoints.toLocaleString("en-IN"),
    },
    {
      id: "level",
      icon: "medal",
      label: "Current Level",
      value: currentLevel?.levelNumber
        ? `${currentLevel.levelNumber}`
        : "Start",
    },
    {
      id: "toGo",
      icon: "arrowRight",
      label: "Points to Next Level",
      value: nextLevel
        ? nextLevel.pointsToGo.toLocaleString("en-IN")
        : "Maxed out",
    },
    {
      id: "cureCoins",
      icon: "wallet",
      label: "CureCoin Balance",
      value: (totalCoins ?? 0).toLocaleString("en-IN"),
    },
  ];

  return {
    totalPoints,
    totalCoins,
    currentLevel,
    nextLevel,
    canClaim,
    claimableOptions,
    milestones,
    currentMilestoneIndex,
    localFrac,
    overallFrac,
    kpiCards,
    quickStats,
  };
};

export const applyHardcodedClaim = (summary) => ({
  ...summary,
  currentLevel: summary.currentLevel
    ? { ...summary.currentLevel, pointsToGo: 0 }
    : summary.currentLevel,
  canClaim: false,
  claimableOptions: [],
});

export const HOW_IT_WORKS = [
  {
    step: 1,
    text: "Every ₹500 of net purchase earns 1 reward point.",
  },
  {
    step: 2,
    text: "Points accumulate automatically as you climb the milestone path.",
  },
  {
    step: 3,
    text: "Reach a milestone to unlock and claim its reward instantly.",
  },
];

const SERVICE_TYPE_ICONS = {
  PHARMACY: "pill",
  KAVACH: "shieldCheck",
};

export const titleCase = (s) =>
  (s || "").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

export const getServiceLabel = (serviceType, subServiceType) => {
  if ((serviceType || "").toUpperCase() === "KAVACH") {
    return titleCase(subServiceType || serviceType);
  }
  return titleCase(serviceType);
};

export const getServiceIcon = (serviceType) =>
  SERVICE_TYPE_ICONS[serviceType] || "gift";

export const getPlanLabel = (serviceType, subServiceType) =>
  (subServiceType || "").trim().toUpperCase() === "KAVACH PLUS"
    ? titleCase(subServiceType)
    : titleCase(serviceType);

export const buildPointsSourceViewModel = (rows) => {
  const sources = (rows || [])
    .map((row, i) => {
      const {
        serviceType,
        subServiceType,
        transactionCount,
        totalCoinsEarned,
        totalPointsEarned,
      } = row;
      return {
        id: `${serviceType}-${subServiceType}-${i}`,
        label: getServiceLabel(serviceType, subServiceType),
        icon: getServiceIcon(serviceType),
        points: totalPointsEarned || 0,
        transactionCount: transactionCount || 0,
        coinsEarned: totalCoinsEarned || 0,
      };
    })
    .sort((a, b) => b.points - a.points);

  const totalPoints = sources.reduce((sum, s) => sum + s.points, 0);

  return { sources, totalPoints };
};

export const buildInsightsViewModel = (insights) => {
  const {
    progressPercent,
    totalCoins,
    nextLevel,
    headline,
    subtext,
    salesNeededByPlan,
    topPerformingPlan,
    kavachSalesCount,
  } = insights;

  const planBreakdown = (salesNeededByPlan || []).map((p) => ({
    label: getPlanLabel(p.serviceType, p.subServiceType),
    salesNeeded: p.salesNeeded,
  }));

  const topPlan = topPerformingPlan
    ? {
        label: getPlanLabel("KAVACH", topPerformingPlan.subServiceType),
        sharePercent: topPerformingPlan.sharePercent,
      }
    : null;

  return {
    progressPercent: progressPercent ?? 0,
    totalCoins: totalCoins ?? 0,
    pointsToGo: nextLevel?.pointsToGo ?? null,
    headline,
    subtext,
    planBreakdown,
    topPlan,
    salesToday: kavachSalesCount?.today ?? 0,
    salesThisWeek: kavachSalesCount?.thisWeek ?? 0,
    salesThisMonth: kavachSalesCount?.thisMonth ?? 0,
  };
};

export const formatTxnDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export const mapTransactionRow = (row) => ({
  id: row.id,
  label: getServiceLabel(row.serviceType, row.subServiceType),
  icon: getServiceIcon(row.serviceType),
  entryType: row.entryType,
  amount: row.transactionAmount || 0,
  pointsEarned: row.pointsEarned || 0,
  coinsEarned: row.coinsEarned || 0,
  pointsBalance: row.pointsClosingBalance,
  coinsBalance: row.coinClosingBalance,
  status: row.status,
  date: formatTxnDate(row.transactionDate),
  externalTransactionId: row.externalTransactionId,
});
